<template>
  <div class="acidifying-home-page">
    <!-- Compact Unified Header Bar: Breadcrumb + Title + Tabs + Actions -->
    <div class="unified-header-bar mb-3">
      <div class="d-flex flex-wrap align-center justify-space-between gap-3">
        <!-- Left: Breadcrumb & Title -->
        <div class="header-left-col">
          <PageBreadcrumb :items="[{ text: 'Old Gold' }, { text: 'Acidifying' }]" class="mb-1" />
          <div class="d-flex align-center">
            <span class="header-status-pill d-none d-md-inline-flex">
              <v-icon size="11" color="#9a6b27" class="mr-1">mdi-gold</v-icon>
              Refining &amp; Thankam Recovery
            </span>
          </div>
        </div>

        <!-- Center: Segmented Navigation Switch -->
        <div class="header-center-col">
          <div class="segmented-control">
            <button
              type="button"
              class="seg-tab-btn"
              :class="{ 'is-active is-outbound': activeTab === 0 }"
              @click="activeTab = 0"
            >
              <v-icon size="15" class="mr-1">mdi-arrow-up-right-bold</v-icon>
              <span>Outbound</span>
              <span class="sub-text d-none d-sm-inline">(Bills to Batch)</span>
            </button>

            <button
              type="button"
              class="seg-tab-btn"
              :class="{ 'is-active is-inbound': activeTab === 1 }"
              @click="activeTab = 1"
            >
              <v-icon size="15" class="mr-1">mdi-flask-round-bottom-outline</v-icon>
              <span>Inbound</span>
              <span class="sub-text d-none d-sm-inline">(Refined Batches)</span>
            </button>
          </div>
        </div>

        <!-- Right: Action Buttons -->
        <div class="header-right-col d-flex align-center gap-2">
          <v-btn
            outlined
            depressed
            small
            class="btn-action-outline"
            @click="$router.push('/OldGoldStatement')"
          >
            <v-icon left size="15" color="#9a6b27">mdi-file-document-outline</v-icon>
            Statement
          </v-btn>

          <v-btn
            depressed
            small
            class="btn-primary-gold"
            @click="$router.push('/AcidingReport')"
          >
            <v-icon left size="15">mdi-file-chart-outline</v-icon>
            Report
          </v-btn>

          <v-btn
            outlined
            depressed
            small
            class="btn-action-outline btn-icon-only"
            @click="refreshCurrentTab()"
            title="Refresh list"
          >
            <v-icon size="16" color="#64748b">mdi-refresh</v-icon>
          </v-btn>
        </div>
      </div>
    </div>

    <!-- Active Tab Content -->
    <div class="tab-body-wrapper">
      <div v-show="activeTab === 0">
        <outbound-list ref="outbound" @batch-created="onBatchCreated" />
      </div>
      <div v-show="activeTab === 1">
        <inbound-list ref="inbound" />
      </div>
    </div>
  </div>
</template>

<script>
import OutboundList from "./OutboundList.vue";
import InboundList from "./InboundList.vue";

export default {
  name: "AcidifyingHome",
  components: { OutboundList, InboundList },
  data() {
    return {
      activeTab: 0,
    };
  },
  methods: {
    onBatchCreated() {
      this.activeTab = 1;
      this.$nextTick(() => {
        if (this.$refs.inbound && typeof this.$refs.inbound.refresh === "function") {
          this.$refs.inbound.refresh();
        }
      });
    },
    refreshCurrentTab() {
      if (this.activeTab === 0 && this.$refs.outbound && typeof this.$refs.outbound.getList === "function") {
        this.$refs.outbound.getList();
      } else if (this.activeTab === 1 && this.$refs.inbound && typeof this.$refs.inbound.refresh === "function") {
        this.$refs.inbound.refresh();
      }
    },
  },
};
</script>

<style scoped>
.acidifying-home-page {
  font-family: "Plus Jakarta Sans", "Inter", -apple-system, BlinkMacSystemFont, sans-serif;
  color: #0f172a;
}

/* Utilities */
.gap-2 { gap: 8px; }
.gap-3 { gap: 12px; }

/* Unified Header Bar */
.unified-header-bar {
  background: #ffffff;
  border: 1px solid #e8e4dc;
  border-radius: 12px;
  padding: 10px 16px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.02);
}

.breadcrumbs-mini {
  font-size: 11.5px;
  color: #64748b;
  font-weight: 500;
  line-height: 1;
}

.sep {
  margin: 0 5px;
  color: #cbd5e1;
}

.active-crumb {
  color: #9a6b27;
  font-weight: 600;
}

.page-title {
  font-size: 19px;
  font-weight: 700;
  color: #0f172a;
  letter-spacing: -0.02em;
  margin: 0;
  line-height: 1.2;
}

.header-status-pill {
  align-items: center;
  font-size: 11px;
  font-weight: 600;
  color: #9a6b27;
  background-color: #fcf8f2;
  border: 1px solid #ebd9be;
  padding: 2px 8px;
  border-radius: 6px;
  letter-spacing: -0.01em;
}

/* Segmented Control in Header */
.segmented-control {
  display: inline-flex;
  background-color: #f1ede7;
  padding: 3px;
  border-radius: 10px;
  border: 1px solid #e5dfd5;
}

.seg-tab-btn {
  display: inline-flex;
  align-items: center;
  padding: 6px 14px;
  border-radius: 7px;
  border: none;
  background: transparent;
  cursor: pointer;
  font-family: "Plus Jakarta Sans", sans-serif;
  font-size: 12.5px;
  font-weight: 600;
  color: #64748b;
  transition: all 0.2s ease;
  user-select: none;
  line-height: 1.2;
}

.seg-tab-btn:hover:not(.is-active) {
  color: #1e293b;
  background-color: rgba(255, 255, 255, 0.5);
}

.seg-tab-btn.is-active {
  background-color: #ffffff;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
}

.seg-tab-btn.is-active.is-outbound {
  color: #b45309;
}

.seg-tab-btn.is-active.is-inbound {
  color: #047857;
}

.sub-text {
  font-size: 11px;
  font-weight: 500;
  opacity: 0.8;
  margin-left: 3px;
}

/* Action Buttons */
.btn-primary-gold {
  height: 34px !important;
  background: linear-gradient(135deg, #b9893f 0%, #875c24 100%) !important;
  color: #ffffff !important;
  font-family: "Plus Jakarta Sans", sans-serif !important;
  font-size: 12.5px !important;
  font-weight: 600 !important;
  text-transform: none !important;
  border-radius: 7px !important;
  padding: 0 14px !important;
  letter-spacing: -0.01em !important;
  box-shadow: 0 2px 4px rgba(154, 106, 44, 0.2) !important;
}

.btn-primary-gold:hover {
  box-shadow: 0 4px 8px rgba(154, 106, 44, 0.3) !important;
}

.btn-action-outline {
  height: 34px !important;
  background-color: #ffffff !important;
  border: 1px solid #e2e8f0 !important;
  color: #475569 !important;
  font-family: "Plus Jakarta Sans", sans-serif !important;
  font-size: 12.5px !important;
  font-weight: 600 !important;
  text-transform: none !important;
  border-radius: 7px !important;
  padding: 0 12px !important;
  transition: all 0.15s ease !important;
}

.btn-action-outline:hover {
  border-color: #9a6b27 !important;
  color: #9a6b27 !important;
  background-color: #fdfaf6 !important;
}

.btn-icon-only {
  min-width: 34px !important;
  width: 34px !important;
  padding: 0 !important;
}
</style>
